import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Users, Settings, UserPlus, Trash2, Crown, Shield, Eye, User,
  Loader2, ArrowLeft, Save, MessageSquare, FolderKanban, ChevronRight,
  Mail, Copy, RefreshCw, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const WorkspaceSettings = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'member' });
  const [inviting, setInviting] = useState(false);
  const [editedWorkspace, setEditedWorkspace] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('general');
  const [regeneratingEmail, setRegeneratingEmail] = useState(false);

  const fetchWorkspace = useCallback(async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}`);
      setWorkspace(response.data);
      setMembers(response.data.members || []);
      setEditedWorkspace({
        name: response.data.name || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Failed to load workspace');
      navigate('/workspaces');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, navigate]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  const handleSaveWorkspace = async () => {
    if (!editedWorkspace.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.patch(`/workspaces/${workspaceId}`, {
        name: editedWorkspace.name.trim(),
        description: editedWorkspace.description.trim()
      });
      toast.success('Workspace updated!');
      refreshWorkspaces();
      fetchWorkspace();
    } catch (error) {
      toast.error('Failed to update workspace');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.email.trim()) return;
    
    setInviting(true);
    try {
      await api.post(`/workspaces/${workspaceId}/invite`, {
        email: inviteData.email.trim(),
        role: inviteData.role
      });
      toast.success(`Invited ${inviteData.email}`);
      setInviteDialogOpen(false);
      setInviteData({ email: '', role: 'member' });
      fetchWorkspace();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      await api.patch(`/workspaces/${workspaceId}/members/${memberId}`, { role: newRole });
      toast.success('Member role updated');
      fetchWorkspace();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this workspace?`)) return;
    
    try {
      await api.delete(`/workspaces/${workspaceId}/members/${memberId}`);
      toast.success('Member removed');
      fetchWorkspace();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!window.confirm(`Are you sure you want to delete "${workspace?.name}"? This will delete all projects and tasks.`)) return;
    
    try {
      await api.delete(`/workspaces/${workspaceId}`);
      toast.success('Workspace deleted');
      refreshWorkspaces();
      navigate('/workspaces');
    } catch (error) {
      toast.error('Failed to delete workspace');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />;
      case 'viewer': return <Eye className="w-4 h-4 text-muted-foreground" />;
      case 'private_contributor': return <EyeOff className="w-4 h-4 text-orange-500" />;
      default: return <User className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      member: 'bg-muted text-muted-foreground',
      viewer: 'bg-muted text-muted-foreground',
      private_contributor: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
    };
    const labels = { private_contributor: 'Private Contributor' };
    return (
      <Badge className={`${colors[role] || 'bg-muted text-muted-foreground'} text-xs`}>
        {getRoleIcon(role)}
        <span className="ml-1">{labels[role] || role}</span>
      </Badge>
    );
  };

  const isOwner = workspace?.user_role === 'owner';
  const isAdmin = workspace?.user_role === 'admin' || isOwner;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="h-full overflow-auto">
        <div className="p-4 md:p-6 max-w-4xl mx-auto pb-20" data-testid="workspace-settings-page">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/workspaces')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">Workspace Settings</h1>
              <p className="text-muted-foreground">{workspace?.name}</p>
            </div>
            {getRoleBadge(workspace?.user_role)}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-muted">
              <TabsTrigger value="general" data-testid="general-tab">
                <Settings className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger value="members" data-testid="members-tab">
                <Users className="w-4 h-4 mr-2" />
                Members ({members.length})
              </TabsTrigger>
              <TabsTrigger value="chat" data-testid="chat-tab">
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat
              </TabsTrigger>
            </TabsList>

            {/* General Settings */}
            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>Update your workspace name and description</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Workspace Name</Label>
                    <Input
                      value={editedWorkspace.name}
                      onChange={(e) => setEditedWorkspace({ ...editedWorkspace, name: e.target.value })}
                      placeholder="Workspace name"
                      disabled={!isAdmin}
                      data-testid="ws-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={editedWorkspace.description}
                      onChange={(e) => setEditedWorkspace({ ...editedWorkspace, description: e.target.value })}
                      placeholder="What's this workspace for?"
                      disabled={!isAdmin}
                      data-testid="ws-desc-input"
                    />
                  </div>
                  
                  {isAdmin && (
                    <Button
                      onClick={handleSaveWorkspace}
                      disabled={saving}
                      className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                      data-testid="save-ws-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Email-to-Task */}
              {workspace?.workspace_type !== 'personal' && (
                <Card className="mt-6" data-testid="email-to-task-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#4A9CC7]" />Email-to-Task</CardTitle>
                    <CardDescription>Send emails to this address to automatically create tasks in this workspace</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Workspace Email Address</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border rounded-md px-3 py-2">
                          <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-mono select-all truncate" data-testid="workspace-email-address">
                            {workspace?.email_address || 'Not configured'}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(workspace?.email_address || '');
                            toast.success('Email address copied!');
                          }}
                          data-testid="copy-email-btn"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            disabled={regeneratingEmail}
                            onClick={async () => {
                              setRegeneratingEmail(true);
                              try {
                                const res = await api.post(`/workspaces/${workspaceId}/regenerate-email`);
                                setWorkspace(prev => ({ ...prev, email_address: res.data.email_address }));
                                toast.success('Email address regenerated');
                              } catch { toast.error('Failed to regenerate'); }
                              finally { setRegeneratingEmail(false); }
                            }}
                            data-testid="regenerate-email-btn"
                          >
                            {regeneratingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">How it works:</p>
                      <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                        <li><strong>Subject</strong> becomes the task title, <strong>Body</strong> becomes the description</li>
                        <li>Use <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">[ProjectName] Your task title</code> to assign a project</li>
                        <li>Replies in the same email thread are added as <strong>comments</strong></li>
                        <li>Duplicate emails are automatically ignored</li>
                        <li>Only workspace members' emails create assigned tasks</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Danger Zone */}
              {isOwner && workspace?.workspace_type !== 'personal' && (
                <Card className="mt-6 border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-600">Danger Zone</CardTitle>
                    <CardDescription>Irreversible actions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleDeleteWorkspace}
                      data-testid="delete-ws-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Workspace
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      This will permanently delete the workspace and all its projects and tasks.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Members */}
            <TabsContent value="members">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Members</CardTitle>
                    <CardDescription>Manage workspace members and their roles</CardDescription>
                  </div>
                  {isAdmin && workspace?.workspace_type !== 'personal' && (
                    <Button
                      onClick={() => setInviteDialogOpen(true)}
                      className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                      data-testid="invite-member-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite Member
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {members.map((member) => (
                      <div 
                        key={member.user_id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50"
                        data-testid={`member-${member.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-[#4A9CC7] text-white">
                              {member.name?.charAt(0) || member.email?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground">{member.name || 'Unknown'}</p>
                            <p className="text-sm text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Role selector (owner can change roles) */}
                          {isOwner && member.role !== 'owner' ? (
                            <Select
                              value={member.role}
                              onValueChange={(value) => handleUpdateMemberRole(member.user_id, value)}
                            >
                              <SelectTrigger className="w-[120px]" data-testid={`role-select-${member.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="private_contributor">Private Contributor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            getRoleBadge(member.role)
                          )}
                          
                          {/* Remove button (owner can remove non-owners) */}
                          {isOwner && member.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:bg-red-50"
                              onClick={() => handleRemoveMember(member.user_id, member.name)}
                              data-testid={`remove-member-${member.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {members.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                        <p>No members yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chat Settings */}
            <TabsContent value="chat">
              <Card>
                <CardHeader>
                  <CardTitle>Workspace Chat</CardTitle>
                  <CardDescription>Collaborate with your team in real-time</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-gradient-to-r from-[#4A9CC7]/10 to-blue-50 border">
                    <div className="flex items-center gap-3 mb-3">
                      <MessageSquare className="w-8 h-8 text-[#4A9CC7]" />
                      <div>
                        <h3 className="font-semibold text-foreground">Real-time Chat</h3>
                        <p className="text-sm text-muted-foreground">Communicate with workspace members instantly</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/chat?workspace=${workspaceId}`)}
                      className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                      data-testid="open-chat-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Open Chat
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    <p>- All workspace members can participate in chat</p>
                    <p>- Messages are saved and searchable</p>
                    <p>- Share files and links with your team</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Invite Dialog */}
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#4A9CC7]" />
                  Invite Member
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="colleague@example.com"
                    data-testid="invite-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={inviteData.role} 
                    onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                  >
                    <SelectTrigger data-testid="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Admin - Can manage workspace
                        </div>
                      </SelectItem>
                      <SelectItem value="member">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Member - Can create tasks
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Viewer - Read only
                        </div>
                      </SelectItem>
                      <SelectItem value="private_contributor">
                        <div className="flex items-center gap-2">
                          <EyeOff className="w-4 h-4" />
                          Private Contributor - Limited visibility
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleInviteUser}
                  disabled={inviting || !inviteData.email.trim()}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  data-testid="send-invite-btn"
                >
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send Invite
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AppLayout>
  );
};

export default WorkspaceSettings;
